import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { userService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  UserGroupIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { Spinner } from '../components/Spinner';

export const Salespersons = () => {
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({
    search: '',
    status: 'active',
    role: 'sales_executive',
    page: 1,
  });
  const [showFilters, setShowFilters] = useState(false);

  const { data: salespersonsData, isLoading } = useQuery(
    ['salespersons', filters],
    () => userService.getUsers(filters),
    {
      keepPreviousData: true,
    }
  );

  const deleteSalespersonMutation = useMutation(userService.deleteUser, {
    onSuccess: () => {
      queryClient.invalidateQueries('salespersons');
      queryClient.invalidateQueries('users');
    },
  });

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this salesperson?')) {
      await deleteSalespersonMutation.mutateAsync(id);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      page: 1, // Reset to page 1 when filters change
    }));
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Salespersons</h1>
            <p className="mt-2 text-gray-600">Manage your sales team members</p>
          </div>
          {hasPermission('users', 'create') && (
            <Link to="/users/new?role=sales_executive" className="btn-primary">
              <PlusIcon className="h-4 w-4 mr-2" />
              Add Salesperson
            </Link>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg mb-6">
        <div className="px-4 py-4 sm:px-6 sm:flex sm:items-center sm:justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-4">
              <div className="flex-1">
                <label className="sr-only">Search</label>
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="input"
                  placeholder="Search salespersons..."
                />
              </div>
              <div>
                <label className="sr-only">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="input"
                >
                  <option value="">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
          </div>
          <div className="mt-4 sm:mt-0 sm:ml-4">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="btn-outline-secondary"
            >
              <MagnifyingGlassIcon className="h-4 w-4 mr-2" />
              Filters
            </button>
          </div>
        </div>

        {/* Filter Options (shown when filters is toggled) */}
        {showFilters && (
          <div className="px-4 py-3 border-t border-gray-200">
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="input"
                >
                  <option value="">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Salespersons Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center">
            <Spinner size="lg" />
            <p className="mt-2 text-gray-500">Loading salespersons...</p>
          </div>
        ) : salespersonsData?.data?.users?.length === 0 ? (
          <div className="p-8 text-center">
            <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No salespersons found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {filters.search || filters.status
                ? 'Try adjusting your filters'
                : 'Get started by creating a new salesperson'}
            </p>
            {hasPermission('users', 'create') && (
              <div className="mt-6">
                <Link to="/users/new?role=sales_executive" className="btn-primary">
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Add Salesperson
                </Link>
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="table">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell">User</th>
                    <th className="table-header-cell">Contact</th>
                    <th className="table-header-cell">Role</th>
                    <th className="table-header-cell">Status</th>
                    <th className="table-header-cell">Leads Assigned</th>
                    <th className="table-header-cell">Created</th>
                    <th className="table-header-cell">Actions</th>
                  </tr>
                </thead>
                <tbody className="table-body">
                  {salespersonsData?.data?.users?.map((salesperson) => (
                    <tr key={salesperson._id} className="table-row">
                      <td className="table-cell">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                              <ShieldCheckIcon className="h-6 w-6 text-gray-600" />
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {salesperson.firstName} {salesperson.lastName}
                            </div>
                            <div className="text-sm text-gray-500">
                              {salesperson.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="text-sm text-gray-900">
                          {salesperson.email}
                        </div>
                        {salesperson.phone && (
                          <div className="text-sm text-gray-500">
                            {salesperson.phone}
                          </div>
                        )}
                      </td>
                      <td className="table-cell">
                        <span className={`badge ${
                          salesperson.role === 'sales_executive' ? 'badge-primary' :
                          salesperson.role === 'manager' ? 'badge-warning' :
                          'badge-secondary'
                        }`}>
                          {salesperson.role.replace('_', ' ').toUpperCase()}
                        </span>
                      </td>
                      <td className="table-cell">
                        <span className={`badge ${
                          salesperson.status === 'active' ? 'badge-success' : 'badge-secondary'
                        }`}>
                          {salesperson.status}
                        </span>
                      </td>
                      <td className="table-cell">
                        <div className="text-sm text-gray-900">
                          {/* TODO: Add lead count query */}
                          0 leads
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="text-sm text-gray-500">
                          {format(new Date(salesperson.createdAt), 'MM/dd/yyyy')}
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="flex space-x-2">
                          {hasPermission('users', 'update') && (
                            <Link to={`/users/${salesperson._id}/edit`} className="text-indigo-600 hover:text-indigo-900">
                              <PencilIcon className="h-4 w-4" />
                            </Link>
                          )}
                          {hasPermission('users', 'delete') && (
                            <button
                              onClick={() => handleDelete(salesperson._id)}
                              className="text-danger-600 hover:text-danger-900"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
